import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #updateToken = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const updateToken = ++this.#updateToken;
    await this.#waitForGalleryImage(newMediaGallery);
    if (updateToken !== this.#updateToken || !this.isConnected) return;

    this.#replaceWithReadyGallery(newMediaGallery);
  };

  /**
   * Waits until the incoming gallery's first image can paint, preventing a white flash during variant swaps.
   *
   * @param {Element} gallery
   */
  async #waitForGalleryImage(gallery) {
    const image = /** @type {HTMLImageElement | null} */ (gallery.querySelector('.product-media__image, img'));
    if (!image || image.complete) return;

    await Promise.race([
      image.decode ? image.decode().catch(() => undefined) : undefined,
      new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      }),
      new Promise((resolve) => setTimeout(resolve, 1200)),
    ]);
  }

  /**
   * @param {Element} newMediaGallery
   */
  #replaceWithReadyGallery(newMediaGallery) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.replaceWith(newMediaGallery);
      return;
    }

    const gallery = /** @type {HTMLElement} */ (newMediaGallery);
    const overlay = this.#createGalleryOverlay();
    this.replaceWith(gallery);

    if (overlay) this.#fadeOutOverlay(overlay);
  }

  /**
   * @returns {HTMLElement | null}
   */
  #createGalleryOverlay() {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const overlay = /** @type {HTMLElement} */ (this.cloneNode(true));
    overlay.removeAttribute('id');
    overlay.style.position = 'fixed';
    overlay.style.inset = 'auto';
    overlay.style.top = `${rect.top}px`;
    overlay.style.left = `${rect.left}px`;
    overlay.style.width = `${rect.width}px`;
    overlay.style.height = `${rect.height}px`;
    overlay.style.margin = '0';
    overlay.style.overflow = 'hidden';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '2147483647';
    overlay.style.transition = 'opacity 180ms ease';
    overlay.style.opacity = '1';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);
    return overlay;
  }

  /**
   * @param {HTMLElement} overlay
   */
  #fadeOutOverlay(overlay) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        overlay.style.opacity = '0';
        window.setTimeout(() => overlay.remove(), 220);
      });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
